import { ParsedReportParams, ReportBackendTypes } from '@ais-ui/websockets';
import { ReportType } from '../../../reportsTypes';
import { useCallback } from 'react';
import {
  findShapeTypeFromReport,
  findShapeTypeFromKeyTrips,
} from '../../utils/findShapeTypeFromKey';
import { GeoJson } from '@ais-ui/api-spec/shapes';

const parseNormalTripsShape = (
  report: ParsedReportParams | undefined,
  firstKey: string,
  secondKey: string
): string => {
  return report?.params[firstKey][secondKey].shape_id;
};

const parseNormalShape = (
  report: ParsedReportParams | undefined,
  backendKey: string
): string => {
  return report?.params[backendKey].shape_id;
};

const parseMultiShape = (
  report: ParsedReportParams | undefined,
  backendKey: string
): string[] => {
  const featureCollection = report?.params[backendKey]?.shape
    ? typeof report.params[backendKey].shape === 'string'
      ? JSON.parse(report.params[backendKey].shape)
      : report.params[backendKey].shape
    : null;

  const features = featureCollection?.features;
  let shapeIds: string[] = [];
  if (features) {
    shapeIds = features.map(
      (feature: GeoJson) => feature.properties.id as string
    );
  }
  return shapeIds;
};

const parsePorts = (
  report: ParsedReportParams | undefined,
  backendKey: string
): number[] => {
  return report?.params[backendKey];
};

const getInitialShapeIds = (
  report: ParsedReportParams | undefined,
  reportType: ReportType
): Record<string, string | string[] | boolean> => {
  const initialShapeType = findShapeTypeFromReport(report);
  const shapeObject: Record<string, string | string[] | boolean> = {};
  if (!initialShapeType || !reportType.shape) {
    return shapeObject;
  }

  const shapeItemsArray = reportType.shape[initialShapeType];
  if (!shapeItemsArray) {
    return shapeObject;
  }

  for (const shapeItem of shapeItemsArray) {
    if (initialShapeType === 'shape') {
      const shapeId = parseNormalShape(report, shapeItem.backendKey);
      shapeObject[shapeItem.name] = shapeId;
    } else if (initialShapeType === 'multiShape') {
      const shapeIds = parseMultiShape(report, shapeItem.backendKey);
      shapeObject[shapeItem.name] = shapeIds;
    } else if (initialShapeType === 'ports') {
      const portIds = parsePorts(report, shapeItem.backendKey);
      shapeObject[shapeItem.name] = portIds.map((portId) => portId.toString());
    } else if (initialShapeType === 'worldShape') {
      shapeObject[shapeItem.name] = true;
    }
  }

  return shapeObject;
};

const getInitialShapeIdsTrips = (
  report: ParsedReportParams | undefined,
  reportType: ReportType
): Record<string, string | string[]> => {
  const shapeObject: Record<string, string | string[]> = {};
  const initialShapeType = findShapeTypeFromKeyTrips(report, 'DESTGAC', 'GAC');

  if (!initialShapeType || !reportType.shape) {
    return shapeObject;
  }

  const shapeItemsArray = reportType.shape[initialShapeType];
  if (!shapeItemsArray) {
    return shapeObject;
  }

  for (const shapeItem of shapeItemsArray) {
    if (initialShapeType === 'shape') {
      const shapeId = parseNormalTripsShape(
        report,
        shapeItem.backendKey,
        'GAC'
      );
      shapeObject[shapeItem.name] = shapeId;
    }
  }

  return shapeObject;
};

export function useInitializeShape(
  report: ParsedReportParams | undefined,
  reportType: ReportType
) {
  return useCallback((): Record<string, any> => {
    switch (reportType.type) {
      case ReportBackendTypes.TRIPS:
        return getInitialShapeIdsTrips(report, reportType);
      default:
        return getInitialShapeIds(report, reportType);
    }
  }, [report, reportType]);
}
